import molecule from '@/assets/molecule.svg';
import molecule3d from '@/assets/molecule3d.svg';
import DraggableDropdownTab from '@/components/DraggableDropdownTab';
import EditorTabsPlaceholder from '@/components/editorTabsPlaceholder';
import { BottomTabsKey, useTabKeyStore } from '@/utils/bottomTabsKeyStore';
import { FileEditorMapService } from '@/utils/fileEditorMapService';
import useFileStore, { Pane, PaneFunction, PaneType } from '@/utils/fileStore';
import { useJobStore } from '@/utils/jobService';
import { saveFileDialogService } from '@/utils/saveFileDialogService';
import { useSplitterStore } from '@/utils/splitterStore';
import { usePermissionsStore } from '@/utils/permissionsStore';
import { CaretRightOutlined, EditOutlined, FileImageOutlined, FilePdfOutlined, FileOutlined, HomeOutlined, LoadingOutlined, MoreOutlined, PythonOutlined, ReloadOutlined, SaveOutlined, SwapOutlined } from '@ant-design/icons';
import { DndContext, PointerSensor, closestCenter, useSensor } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { SortableContext, horizontalListSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useRequest } from 'ahooks';
import { App, Button, Checkbox, GetProp, MenuProps, Modal, Space, Tabs } from 'antd';
import dayjs from 'dayjs';
import pathbe from 'path-browserify-esm';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import EditorTabsCloseButton from './editorTabsCloseButton';
import FileEditor, { FileEditorRef } from './fileEditor';
import ImageViewer from './imageViewer';
import JsmolDisplay from './jsmolDisplay';
import PDFViewer from './pdfViewer';
import ResizableContainer from './resizableContainer';
import ExcelViewer from './excelViewer';
import WordViewer from './wordViewer';
import { unsecuredCopyToClipboard } from '@/utils/clipboard';
import { openDeniedDialog } from './deniedDialog';
import { fetchUserPermissions } from '@/utils/permissionsService';
import { getLaTeXCompileRequestFeedback } from '@/utils/latexCompileNotifications';
import * as LaTeXApi from '@/api/latex';
import { useLatexCompileStore } from '@/utils/latexCompileStore';
import { useTranslation } from 'react-i18next';
import { MarkdownRenderInEditor } from './markdownRender';
import { AitomolDisplay } from './aitomol';
import { KetcherDisplay } from './ketcherDisplay';
import ConvertDialog from './convertDialog';
import UploadDialog from './uploadDialog';
import { fileService } from '@/utils/filesService';
import { UploadOutlined } from '@ant-design/icons';
export interface EditorPane {
    label: string;
    path?: string;
    closeIcon: React.ReactNode;
    children: React.ReactNode;
    key: string;
    modified: boolean;
}


const ExtraButtonGroup = (params: {
    pane?: Pane,
    openChatBot: () => void,
    openHome: () => void,
    openInJSMOL: (path: string) => void
    openInAitomol: (path: string) => void
    openInKetcher: (path: string) => void
    openJobSubmission: () => void;
    handleSave: (paneKey?: string) => Promise<boolean>;
    jobDefaultSubmit: () => Promise<void>;
    onConvertEnd: () => void;
}) => {
    const { pane, jobDefaultSubmit, openHome, openInJSMOL, openInAitomol, openInKetcher, openJobSubmission, handleSave, onConvertEnd } = params;
    const { message } = App.useApp();
    const permissions = usePermissionsStore(state => state.permissions);
    const { t } = useTranslation('common');

    const isMarkdownRender = useFileStore(state => state.isMarkdownRender);
    const setMarkdownRender = useFileStore(state => state.setMarkdownRender);

    const [convertModalOpen, setConvertModalOpen] = useState(false);
    const [molEditorModalOpen, setMolEditorModalOpen] = useState(false);

    const isMarkdownRenderChecked = useMemo(() => {
        return <div key={PaneFunction.MD_RENDER} className=' pl-2 pr-4 '>
            <Checkbox checked={isMarkdownRender} onChange={(e) => {
                setMarkdownRender(e.target.checked);
            }}>
                Markdown Preview
            </Checkbox>
        </div>
    }, [isMarkdownRender]);

    const { run, loading } = useRequest(jobDefaultSubmit, {
        manual: true
    })

    const compilingPaths = useLatexCompileStore(state => state.compilingPaths);
    const startCompile = useLatexCompileStore(state => state.startCompile);

    const compileLaTex = async (filePath: string) => {
        startCompile(filePath);
        const result = await LaTeXApi.compileLaTeX(filePath);
        const feedback = getLaTeXCompileRequestFeedback(result, t);
        message[feedback.level](feedback.content);
        if (!result?.ok) {
            useLatexCompileStore.getState().finishCompile(filePath);
        }
    };





    if (!pane?.paneFunctions?.length) {
        return <>
            <Button type='text' onClick={() => openHome()}>
                <HomeOutlined />
            </Button>
        </>
    }

    return <div className=' flex items-center'>
        <Button type='text' onClick={() => openHome()}>
            <HomeOutlined />
        </Button>
        {
            pane?.paneFunctions.map((type) => {
                if (type === PaneFunction.SAVE) {
                    return <Button key={type} onClick={async () => await handleSave()} className=' w-6 relative' type="text">
                        <SaveOutlined />
                    </Button>
                } 
                else if (type === PaneFunction.CONVERT) {
                    return <React.Fragment key={PaneFunction.CONVERT}>
                        <Button
                            className=' w-6 relative'
                            type="text"
                            onClick={() => setConvertModalOpen(true)}
                            title="Convert file format"
                        >
                            <SwapOutlined />
                        </Button>
                        {pane?.path && (
                            <ConvertDialog
                                open={convertModalOpen}
                                sourcePath={pane.path}
                                onCancel={() => setConvertModalOpen(false)}
                                onSuccess={() => {
                                    setConvertModalOpen(false);
                                    onConvertEnd();
                                }}
                            />
                        )}
                    </React.Fragment>
                }
                else if (type === PaneFunction.SUBMIT) {
                    const filePath = (pane?.path || '').toLowerCase();
                    if (filePath.endsWith('.py')) {
                        const canAccessSubmissionsPython = permissions['submissions_python_scripts'] === true;
                        if (!canAccessSubmissionsPython) {
                            return <div key={PaneFunction.SUBMIT}>
                                <Button className=' w-6 relative' type="text" onClick={openDeniedDialog}>
                                    {loading ? <LoadingOutlined /> : <CaretRightOutlined />}
                                </Button>
                                <Button className=' w-6 relative' type="text" onClick={openDeniedDialog}>
                                    <MoreOutlined className=' absolute right-0' />
                                    <CaretRightOutlined />
                                </Button>
                            </div>
                        }
                    }

                    return <div key={PaneFunction.SUBMIT}>
                        <Button className=' w-6 relative' type="text" onClick={run}>
                            {loading ? <LoadingOutlined /> : <CaretRightOutlined />}
                        </Button>
                        <Button className=' w-6 relative' type="text" onClick={openJobSubmission}>
                            <MoreOutlined className=' absolute right-0' />
                            <CaretRightOutlined />
                        </Button>
                    </div>
                } else if (type === PaneFunction.GUI) {
                    // Every molecular file gets the same chooser: the aitomol 3D
                    // viewer (new), the JSmol iframe (legacy, kept until the new
                    // module has proven itself on real job output) and Ketcher 2D
                    // when the file is one Ketcher can read.
                    const hasKetcher = pane?.paneFunctions?.includes(PaneFunction.KETCHER);
                    return <React.Fragment key={type}>
                        <Button type='text'
                            onClick={() => setMolEditorModalOpen(true)}
                            className=' w-8 relative'
                            title={t('aitomol.open_viewer')}>
                            <img className='svg-icon-dark absolute m-auto top-0 -left-1 bottom-0 right-0 w-4' src={hasKetcher ? molecule : molecule3d} alt="" />
                        </Button>
                        <Modal
                            title={t('aitomol.open_viewer')}
                            open={molEditorModalOpen}
                            onCancel={() => setMolEditorModalOpen(false)}
                            footer={null}
                            width={480}
                        >
                            <div className='flex gap-3 justify-center py-4 flex-wrap'>
                                <Button onClick={() => { pane?.path && openInAitomol(pane.path); setMolEditorModalOpen(false); }}>
                                    <img src={molecule3d} className='svg-icon-dark w-4 inline mr-1' alt="" />
                                    {t('aitomol.viewer_3d_new')}
                                </Button>
                                <Button onClick={() => { pane?.path && openInJSMOL(pane.path); setMolEditorModalOpen(false); }}>
                                    <img src={molecule3d} className='svg-icon-dark w-4 inline mr-1' alt="" />
                                    {t('aitomol.viewer_3d_jsmol')}
                                </Button>
                                {hasKetcher ? (
                                    <Button onClick={() => { pane?.path && openInKetcher(pane.path); setMolEditorModalOpen(false); }}>
                                        <img src={molecule} className='svg-icon-dark w-4 inline mr-1' alt="" />
                                        {t('aitomol.viewer_2d')}
                                    </Button>
                                ) : null}
                            </div>
                        </Modal>
                    </React.Fragment>;
                } else if (type === PaneFunction.KETCHER) {
                    const hasGUI = pane?.paneFunctions?.includes(PaneFunction.GUI);
                    if (hasGUI) return null;
                    return <Button key={type} type='text'
                        onClick={() => pane?.path && openInKetcher(pane.path)}
                        className=' w-6 relative'
                        title="Open in 2D Editor">
                        <img className='svg-icon-dark absolute m-auto top-0 -left-1 bottom-0 right-0 w-4' src={molecule} alt="" />
                    </Button>
                } else if (type === PaneFunction.PDFLATEX && pane?.path) {
                    return <Button key={type} type='text' onClick={() => compileLaTex(pane.path!)} loading={!!(pane.path && compilingPaths[pane.path])} className=' w-6 relative'>
                        <FilePdfOutlined />
                    </Button>
                }
                else if (type === PaneFunction.MD_RENDER) {
                    return isMarkdownRenderChecked
                }
            })
        }
        {/* {
            import.meta.env.VITE_CHAT_BOT && <Button key={'chatbot'} type='text' disabled={!pane?.path} className=' w-6 relative' onClick={openChatBot}>
                <RobotOutlined />
            </Button>
        } */}
    </div>
}

const TabIcon = (props: { type: PaneType, path?: string }) => {
    switch (props.type) {
        case PaneType.TEXT:
        case PaneType.TEMP_FILE:
            if (props.path?.endsWith('.py')) {
                return <PythonOutlined />
            }
            return <FileOutlined />
        case PaneType.IMAGE:
            return <FileImageOutlined />
        case PaneType.PDF:
            return <FilePdfOutlined />
        case PaneType.JSMOL:
            return <img className='svg-icon-dark h-5 pb-1 inline-block' src={molecule} alt="" />
        case PaneType.AITOMOL:
            return <img className='svg-icon-dark h-5 pb-1 inline-block' src={molecule3d} alt="" />
        case PaneType.KETCHER:
            return <img className='svg-icon-dark h-5 pb-1 inline-block' src={molecule} alt="" />
        case PaneType.EXCEL:
            return <FileOutlined />
        case PaneType.WORD:
            return <FileOutlined />
        case PaneType.Home:
            return <HomeOutlined />
        // case PaneType.CHAT_BOT:
        //     return <RobotOutlined />
    }
}

const EditorTabs = ({ openJobSubmission }: { openJobSubmission: () => void }) => {
    const { modal, message } = App.useApp();

    const panes = useFileStore(state => state.panes);
    const { t } = useTranslation('common');

    const openChatBot = useFileStore(state => state.openChatBot);
    const openHome = useFileStore(state => state.openHome);
    const activePanes = useFileStore(state => state.activePanes);
    const onTabChange = useFileStore(state => state.onTabChange);
    const onCloseTab = useFileStore(state => state.onCloseTab);
    const onTabMove = useFileStore(state => state.onTabMove);

    const openInJSMOL = useFileStore(state => state.openInJSMOL);
    const openInAitomol = useFileStore(state => state.openInAitomol);
    const openInKetcher = useFileStore(state => state.openInKetcher);
    const onFileChange = useFileStore(state => state.onFileChange);
    const onFileSave = useFileStore(state => state.onFileSave);

    const createTempFile = useFileStore(state => state.createTempFile);

    const navigateToFile = useFileStore((state) => state.navigateToFile);

    const [refreshKeys, setRefreshKeys] = useState<Record<string, number>>({});

    const refreshPane = (key: string) => {
        setRefreshKeys(prev => ({ ...prev, [key]: (prev[key] ?? 0) + 1 }));
    };

    const isMarkdownRender = useFileStore(state => state.isMarkdownRender);

    const setFileEditorRef = (key: string, ref: FileEditorRef) => {
        FileEditorMapService.getInstance().setEditorRef(key, ref);
    }


    const textContentRender = useCallback((pane: Pane, refreshKey: number) => {
        if (pane.path?.endsWith('.md') && isMarkdownRender) {
            return (
                <ResizableContainer>
                    <MarkdownRenderInEditor key={refreshKey} path={pane.path} />
                </ResizableContainer>
            )
        }
        return (
            <ResizableContainer>
                <FileEditor key={refreshKey} fileName={pane.label} pane={pane} path={pane.path} ref={(ref) => ref && setFileEditorRef(pane.key, ref)} onChange={() => onFileChange(pane.key)} />
            </ResizableContainer>
        )
    }, [isMarkdownRender]);

    const ContentGenerateHandler = (pane: Pane) => {
        const refreshKey = refreshKeys[pane.key] ?? 0;
        switch (pane.type) {
            case PaneType.TEXT:
                return textContentRender(pane, refreshKey);
            case PaneType.TEMP_FILE:
                return (
                    <ResizableContainer>
                        <FileEditor key={refreshKey} fileName={pane.label} pane={pane} path={pane.path} ref={(ref) => ref && setFileEditorRef(pane.key, ref)} onChange={() => onFileChange(pane.key)} />
                    </ResizableContainer>
                )
            case PaneType.IMAGE:
                return (
                    <ResizableContainer>
                        {pane?.path && <ImageViewer key={refreshKey} filePath={pane.path} />}
                    </ResizableContainer>
                )
            case PaneType.PDF:
                return (
                    <ResizableContainer>
                        {pane?.path && <PDFViewer key={refreshKey} filePath={pane.path} />}
                    </ResizableContainer>
                )
            case PaneType.JSMOL:
                return (
                    <ResizableContainer>
                        {pane?.path && <JsmolDisplay key={refreshKey} path={pane.path} />}
                    </ResizableContainer>
                )
            case PaneType.AITOMOL:
                return (
                    <ResizableContainer>
                        {<AitomolDisplay key={refreshKey} path={pane.path} />}
                    </ResizableContainer>
                )
            case PaneType.KETCHER:
                return (
                    <ResizableContainer>
                        <KetcherDisplay key={refreshKey} path={pane.path} />
                    </ResizableContainer>
                )
            case PaneType.EXCEL:
                return (
                    <ResizableContainer>
                        <ExcelViewer key={refreshKey} filePath={pane.path} />
                    </ResizableContainer>
                )
            case PaneType.WORD:
                return (
                    <ResizableContainer>
                        <WordViewer key={refreshKey} filePath={pane.path} />
                    </ResizableContainer>
                )
            case PaneType.Home:
                return (
                    <ResizableContainer>
                        <EditorTabsPlaceholder key={refreshKey} />
                    </ResizableContainer>
                )
            // case PaneType.CHAT_BOT:
            //     return (
            //         <ResizableContainer>
            //             <iframe className=' w-full h-full' src={import.meta.env.VITE_CHAT_BOT}></iframe>
            //         </ResizableContainer>
            //     )
        }

    }

    const editorPanes: EditorPane[] = panes.map((pane) => {
        return {
            ...pane,
            children: ContentGenerateHandler(pane),
            closeIcon: <EditorTabsCloseButton hasModified={pane.modified} />,
            icon: <TabIcon type={pane.type} path={pane.path} />,
        }
    })

    const sensor = useSensor(PointerSensor, { activationConstraint: { distance: 5 }, coordinateGetter: sortableKeyboardCoordinates });
    const submitJob = useJobStore(state => state.submitJob);
    const {
        setVerticalSizes,
    } = useSplitterStore();
    const setBottomTabsActiveKey = useTabKeyStore(state => state.setBottomTabsActiveKey);
    const setOutputTabsActiveKey = useTabKeyStore(state => state.setOutputTabsActiveKey);

    const [isConverting, setIsConverting] = useState(false);
    const [addTabModalOpen, setAddTabModalOpen] = useState(false);
    const [uploadDialogVisible, setUploadDialogVisible] = useState(false);
    const activeFolderPath = useFileStore(state => state.activeFolderPath);
    const baseHome = useFileStore(state => state.baseHome);

    // Warn user before closing the page while a conversion is in progress
    useEffect(() => {
        if (!isConverting) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isConverting]);

    const submitChecker = async (pane: Pane): Promise<boolean> => {
        return new Promise<boolean>((resolve, _reject) => {
            const latestPane = pane?.key
                ? useFileStore.getState().panes.find(item => item.key === pane.key)
                : useFileStore.getState().activePanes[0];

            if (!latestPane) {
                message.error(t('editor_tabs.no_file_selected'));
                resolve(false);
                return;
            }

            if (latestPane.type === PaneType.TEMP_FILE) {
                message.info(t('editor_tabs.save_before_submit'));
                resolve(false);
                return;
            }

            if (latestPane.modified) {
                modal.confirm({
                    title: t('editor_tabs.submit_confirm_title'),
                    content: t('editor_tabs.submit_confirm_content'),
                    okText: t('editor_tabs.save'),
                    cancelText: t('editor_tabs.cancel'),
                    onOk: async () => {
                        const isSuccess = await handleSave(latestPane.key);
                        if (isSuccess) {
                            resolve(true);
                        } else {
                            message.error(t('editor_tabs.save_before_submit_failed'));
                            resolve(false);
                        }
                    },
                    onCancel: () => {
                        resolve(false);
                    }
                });
            } else {
                resolve(true);
            }
        })
    }

    const jobSubmission = async () => {
        const currentActivePanes = useFileStore.getState().activePanes;
        const currentPane = currentActivePanes[0];
        const isPassed = await submitChecker(currentPane);

        if (!isPassed) {
            return;
        }
        openJobSubmission();
    }

    const updateFolder = useFileStore(state => state.updateFolder);

    const jobSubmissionWithDefault = async () => {
        const currentActivePanes = useFileStore.getState().activePanes;
        const currentPane = currentActivePanes[0];
        const isPassed = await submitChecker(currentPane);

        if (!isPassed) {
            return;
        }

        const selectedFilePath = activePanes[0].path;
        if (!selectedFilePath) {
            message.error(t('editor_tabs.no_file_selected'));
            return;
        }

        const user = localStorage.getItem('userName');
        const tag = user + "_" + dayjs().unix().toString();

        const jobName = selectedFilePath.split("/").pop() ?? ""
        const isSuccess = await submitJob({
            path: selectedFilePath,
            cpu_num: 1,
            gpu_num: 0,
            memory: 1024,
            jobName: jobName,
            comment: tag
        })
        updateFolder(pathbe.dirname(selectedFilePath))
        if (isSuccess) {
            message.success(t('editor_tabs.job_submitted'));
            setVerticalSizes(["80%", "20%"])
            setBottomTabsActiveKey(BottomTabsKey.OUTPUT)
            setOutputTabsActiveKey(tag)
        }
    }

    if (!editorPanes.length) {
        return <EditorTabsPlaceholder />;
    }

    const handleSave = async (paneKey?: string) => {
        const pane = paneKey
            ? useFileStore.getState().panes.find(item => item.key === paneKey)
            : useFileStore.getState().activePanes?.[0];
        if (!pane?.key) return false;
        if (pane?.path) {
            const success = await onFileSave(pane.key);
            return success;
        } else {
            saveFileDialogService.show(pane.key);
            return true;
        }
    }

    const onEdit: GetProp<typeof Tabs, 'onEdit'> = (targetKey, action: 'add' | 'remove') => {
        if (action === 'remove') {
            const pane = editorPanes.find(pane => pane.key === targetKey);
            // if not found, do nothing
            if (!pane) return
            if (pane?.modified) {
                modal.confirm({
                    title: 'Confirm',
                    content: 'You have unsaved changes. Are you sure you want to close this tab?',
                    okText: 'Save',
                    cancelText: 'Cancel',
                    footer: (
                        <div className=' flex mt-4 flex-row-reverse'>
                            <Space>
                                <Button onClick={() => {
                                    // Close the tab without saving
                                    onCloseTab(pane.key);
                                    Modal.destroyAll();
                                }}>
                                    Don't Save
                                </Button>
                                <Button onClick={() => Modal.destroyAll()}>
                                    Cancel
                                </Button>
                                <Button type="primary" onClick={async () => {
                                    // Save changes and close the tab
                                    const isSuccess = await handleSave(pane.key);
                                    if (isSuccess) {
                                        onCloseTab(pane.key);
                                        Modal.destroyAll();
                                    }
                                }}>
                                    Save
                                </Button>
                            </Space>
                        </div>
                    ),
                });
                return;
            } else {
                onCloseTab(pane.key);
            }
        } else if (action === 'add') {
            setAddTabModalOpen(true);
        }
    };


    const tabMenuGenerate = (key: string): MenuProps['items'] => {
        const pane = panes.find(pane => pane.key === key);
        const items: MenuProps['items'] = [
            {
                key: 'refresh',
                label: t('editor_tabs.refresh'),
                icon: <ReloadOutlined />,
            },
            {
                type: 'divider',
            },
            {
                key: 'close_others',
                label: t('editor_tabs.close_other_tabs'),
            },
            {
                key: 'close_all',
                label: t('editor_tabs.close_all_tabs'),
                danger: true,
            }]

        if (pane?.path) {
            items.unshift({
                key: 'copy_absolute_path',
                label: t('editor_tabs.copy_absolute_path'),
            }, {
                key: 'reveal_in_explorer',
                label: t('editor_tabs.reveal_in_explorer'),
            })
        }

        return items;
    }

    const closeAllTabs = () => {
        panes.forEach(pane => {
            onCloseTab(pane.key);
        })
    }

    const closeOtherTabs = (key: string) => {
        panes.forEach(pane => {
            if (pane.key !== key) {
                onCloseTab(pane.key);
            }
        })
    }

    const copyAbsolutePath = (path: string) => {
        if (navigator?.clipboard && window?.isSecureContext) {
            navigator.clipboard.writeText(path).then(() => {
                message.success(t('editor_tabs.path_copied'));
            });
        } else {
            unsecuredCopyToClipboard(path)
        }
    }

    return (
        <>
            <Modal
                open={addTabModalOpen}
                onCancel={() => setAddTabModalOpen(false)}
                footer={null}
                title={t('editor_tabs.new_tab')}
                width={320}
            >
                <div className='flex flex-col gap-3 py-4'>
                    <Button
                        block
                        icon={<FileOutlined />}
                        onClick={() => {
                            createTempFile();
                            setAddTabModalOpen(false);
                        }}
                    >
                        {t('editor_tabs.new_text_file')}
                    </Button>
                    <Button
                        block
                        icon={<img src={molecule} className='w-4 inline mr-1' alt='' />}
                        onClick={() => {
                            openInKetcher();
                            setAddTabModalOpen(false);
                        }}
                    >
                        {t('editor_tabs.open_2d_editor')}
                    </Button>
                    <Button
                        block
                        icon={<UploadOutlined />}
                        onClick={() => {
                            setAddTabModalOpen(false);
                            setUploadDialogVisible(true);
                        }}
                    >
                        {t('editor_tabs.upload_file')}
                    </Button>
                </div>
            </Modal>
            <UploadDialog
                visible={uploadDialogVisible}
                targetDir={activeFolderPath ?? baseHome}
                onClose={() => setUploadDialogVisible(false)}
                onSubmit={fileService.uploadFiles}
            />
            <Tabs
                tabBarExtraContent={{
                    left: <ExtraButtonGroup openHome={openHome} openInAitomol={openInAitomol} openInKetcher={openInKetcher} openChatBot={openChatBot} openJobSubmission={jobSubmission} jobDefaultSubmit={jobSubmissionWithDefault} openInJSMOL={openInJSMOL} handleSave={handleSave} pane={activePanes[0]} onConvertEnd={() => setIsConverting(false)} />,
                }}
                onChange={onTabChange}
                activeKey={activePanes[0].key}
                type="editable-card"
                size='small'
                items={editorPanes}
                className='editor-tabs'
                renderTabBar={(tabBarProps, DefaultTabBar) => (
                    <DndContext
                        sensors={[sensor]}
                        onDragEnd={onTabMove}
                        collisionDetection={closestCenter}
                        modifiers={[restrictToHorizontalAxis]}
                    >
                        <SortableContext items={editorPanes.map((i) => i.key)} strategy={horizontalListSortingStrategy}>
                            <DefaultTabBar {...tabBarProps}>
                                {(node) => (
                                    <DraggableDropdownTab
                                        data-node-key={node.key as string}
                                        key={node.key}
                                        menuItems={tabMenuGenerate(node.key as string)}
                                        onMenuClick={(key) => {
                                            if (key === 'close' && node.key) {
                                                onEdit(node.key, 'remove');
                                            } else if (key === 'close_all') {
                                                closeAllTabs();
                                            } else if (key === 'close_others' && node.key) {
                                                closeOtherTabs(node.key);
                                            } else if (key === 'copy_absolute_path' && node.key) {
                                                const pane = panes.find(pane => pane.key === node.key);
                                                if (pane?.path) {
                                                    copyAbsolutePath(pane.path);
                                                }
                                            } else if (key === 'reveal_in_explorer' && node.key) {
                                                const pane = panes.find(pane => pane.key === node.key);
                                                if (pane?.path) {
                                                    navigateToFile(pane.path);
                                                }
                                            } else if (key === 'refresh' && node.key) {
                                                refreshPane(node.key);
                                            }
                                        }}
                                    >
                                        <div>{node}</div>
                                    </DraggableDropdownTab>
                                )}
                            </DefaultTabBar>
                        </SortableContext>
                    </DndContext>
                )}
                onEdit={onEdit}
            />
        </>
    );
};

export default EditorTabs;
